import { cn } from './cn';
import type {
  CardProps,
  CardHeaderProps,
  CardTitleProps,
  CardDescriptionProps,
  CardActionProps,
  CardContentProps,
  CardFooterProps,
} from './Card.types';

/**
 * Componente Card del sistema de diseño Siesa
 *
 * Contenedor versátil tipo tarjeta compuesto por 7 sub-componentes:
 * `Card`, `CardHeader`, `CardTitle`, `CardDescription`, `CardAction`,
 * `CardContent` y `CardFooter`.
 *
 * **Estructura típica:**
 * ```
 * Card
 *   ├── CardHeader
 *   │     ├── CardTitle
 *   │     ├── CardDescription
 *   │     └── CardAction (opcional)
 *   ├── CardContent
 *   └── CardFooter (opcional)
 * ```
 *
 * **Tokens de color Siesa utilizados:**
 * - Fondo: `bg-bg-primary` / `dark:bg-dark-bg-secondary`
 * - Texto: `text-content-primary` / `dark:text-dark-content-primary`
 * - Texto secundario: `text-content-secondary` / `dark:text-dark-content-secondary`
 * - Borde: `border-border-primary` / `dark:border-dark-border-primary`
 *
 * **Dark Mode:**
 * Soporte completo mediante las clases `dark:` del sistema de diseño Siesa.
 * Activar añadiendo la clase `dark` al elemento `<html>`.
 *
 * @example
 * ```tsx
 * // Tarjeta básica
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Título de la tarjeta</CardTitle>
 *     <CardDescription>Descripción opcional del contenido</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     <p>Contenido principal de la tarjeta.</p>
 *   </CardContent>
 *   <CardFooter>
 *     <Button type="default" size="base">Guardar</Button>
 *   </CardFooter>
 * </Card>
 *
 * // Tarjeta con acción en el header
 * <Card>
 *   <CardHeader>
 *     <CardTitle>Resumen</CardTitle>
 *     <CardAction>
 *       <Button type="plain" size="sm" iconOnly leftIcon={<EditIcon />} ariaLabel="Editar" />
 *     </CardAction>
 *   </CardHeader>
 *   <CardContent>Contenido</CardContent>
 * </Card>
 * ```
 */
export function Card({ className, ...props }: CardProps) {
  return (
    <div
      data-slot="card"
      className={cn(
        'flex flex-col gap-6 rounded-xl border border-zinc-200 bg-white py-6 shadow-sm',
        'dark:border-zinc-600 dark:bg-zinc-900',
        className
      )}
      {...props}
    />
  );
}

/**
 * Cabecera de la tarjeta con grid layout.
 *
 * Cuando contiene un `CardAction`, el grid cambia automáticamente a 2 columnas
 * para posicionar la acción en la esquina superior derecha.
 * Soporta container queries (`@container/card-header`).
 */
export function CardHeader({ className, ...props }: CardHeaderProps) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        '@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-2 px-6',
        'has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6',
        className
      )}
      {...props}
    />
  );
}

/**
 * Título principal de la tarjeta.
 *
 * Usa tipografía semibold con `leading-none` para alineación precisa.
 */
export function CardTitle({ className, ...props }: CardTitleProps) {
  return (
    <div
      data-slot="card-title"
      className={cn('leading-none font-bold', className)}
      {...props}
    />
  );
}

/**
 * Descripción secundaria de la tarjeta.
 *
 * Renderizada con color de texto secundario del sistema de diseño Siesa.
 */
export function CardDescription({ className, ...props }: CardDescriptionProps) {
  return (
    <div
      data-slot="card-description"
      className={cn(
        'text-sm text-zinc-500 dark:text-zinc-400',
        className
      )}
      {...props}
    />
  );
}

/**
 * Acción posicionada en la esquina superior derecha del `CardHeader`.
 *
 * Debe ser hijo directo de `CardHeader` para que el posicionamiento grid funcione.
 * Se ubica en la primera fila junto al título.
 */
export function CardAction({ className, ...props }: CardActionProps) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-start-1 self-start justify-self-end',
        className
      )}
      {...props}
    />
  );
}

/**
 * Área de contenido principal de la tarjeta.
 *
 * Aplica padding horizontal estándar de 24px (px-6) consistente con el header.
 */
export function CardContent({ className, ...props }: CardContentProps) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-6', className)}
      {...props}
    />
  );
}

/**
 * Pie de la tarjeta con layout flex horizontal.
 *
 * Cuando tiene borde superior (`border-t`), aplica padding vertical automático (pt-6).
 * Ideal para agrupar acciones o información de estado.
 */
export function CardFooter({ className, ...props }: CardFooterProps) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center px-6 [.border-t]:pt-6', className)}
      {...props}
    />
  );
}
